/**
 * Abstract base factory for test data generation.
 * All entity factories MUST extend this class.
 *
 * @template T - The type of object this factory produces
 *
 * Usage:
 *   class UnicornFactory extends ObjectFactory<Unicorn> { ... }
 *   const factory = new UnicornFactory();
 *   factory.build();                          // → valid Unicorn with defaults
 *   factory.build({ name: 'Rainbow' });       // → valid Unicorn with name override
 *   factory.modify(existingUnicorn, { legendary: true }); // → copy with legendary changed
 */
export abstract class ObjectFactory<T> {
  /**
   * Returns the default values for all required fields.
   * Subclasses MUST implement this to define sensible defaults.
   */
  protected abstract getDefaults(): T;

  /**
   * Build a new instance of T with optional overrides.
   * Overrides are spread on top of defaults, so you only specify what differs.
   */
  build(overrides: Partial<T> = {}): T {
    return {
      ...this.getDefaults(),
      ...overrides,
    };
  }

  /**
   * Create a modified copy of an existing object with partial overrides.
   * Useful for testing mutations, updates, or "before/after" scenarios.
   */
  modify(base: T, overrides: Partial<T>): T {
    return {
      ...base,
      ...overrides,
    };
  }
}
